import React, { useEffect, useState } from 'react'
import {
  Accordion, AccordionDetails, AccordionSummary,
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, Link, Rating,
  Step, StepButton, StepContent, Stepper, TextField, Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import PrintIcon from '@mui/icons-material/Print'
import { supabase } from '../../../supabaseClient'
import { useAuth } from '../../../auth/AuthContext'
import { useViewerStudent } from '../../../auth/ViewAsContext'
import { LAB_PROJECTS, LabProject, LabQuestion, LabModel, techniqueFor, applicationLearnMore, DATASET_DETAIL, HYPOTHESIS_VARS, buildColabCode } from './projects'
import { J, Jt, handlePrint, fmtDate } from '../../../components/journal/paper'

// AI Research Lab — a menu of guided learning projects (not graded work) that teach
// how to use AI + a dataset to investigate a scientific question. Trial students may
// work through ONE (the rest lock, as an upsell); enrolled students, unlimited.
// v1 is content-driven (real benchmark figures, no live execution).

const fmtMetric = (p: LabProject, v: number) =>
  p.taskType === 'regression' ? `R² ${v.toFixed(2)}` : `${v}%`

// ── Teaching helpers ────────────────────────────────────────────────────────────

function Teach({ children }: { children: React.ReactNode }) {
  return <Alert severity="info" icon={<InsightsOutlinedIcon fontSize="small" />} sx={{ mt: 1.5, mb: 1 }}>{children}</Alert>
}

function PickList<T extends { }>({ items, index, onPick, render }: {
  items: T[]; index: number | null; onPick: (i: number) => void; render: (item: T, selected: boolean) => React.ReactNode
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      {items.map((it, i) => {
        const selected = index === i
        return (
          <Card key={i} variant="outlined" onClick={() => onPick(i)}
            sx={{ cursor: 'pointer', borderColor: selected ? 'primary.main' : 'divider', borderWidth: selected ? 2 : 1, '&:hover': { borderColor: 'primary.light' } }}>
            <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>{render(it, selected)}</CardContent>
          </Card>
        )
      })}
    </Box>
  )
}

// ── The guided walkthrough (config-driven) ──────────────────────────────────────

function Walkthrough({ project, onComplete, onBack, studentName, studentSchool, studentId }: {
  project: LabProject; onComplete: () => void; onBack: () => void
  studentName?: string; studentSchool?: string; studentId?: string | null
}) {
  const [active, setActive]   = useState(0)
  const [qIdx, setQIdx]       = useState<number | null>(null)
  const [mIdx, setMIdx]       = useState<number | null>(null)
  const [dsHelpOpen, setDsHelpOpen] = useState(false)
  const [learnOpen, setLearnOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [maxStep, setMaxStep] = useState(0)   // furthest step reached — earlier ones are freely revisitable ("what-if")
  const detail = DATASET_DETAIL[project.key]
  const vars = HYPOTHESIS_VARS[project.key]
  const recIdx = project.questions.findIndex(q => q.recommended)
  const chosenQuestion = project.questions[qIdx ?? (recIdx >= 0 ? recIdx : 0)]?.text ?? ''
  const goTo = (i: number) => { setActive(i); setMaxStep(m => Math.max(m, i)) }

  // Report step
  const [baselineInput, setBaselineInput] = useState('')
  const [resultInput, setResultInput] = useState('')

  // Feedback step
  const [fbRating, setFbRating] = useState<number | null>(null)
  const [fbExp, setFbExp]   = useState('')
  const [fbFeat, setFbFeat] = useState('')
  const [fbProj, setFbProj] = useState('')
  const [fbBusy, setFbBusy] = useState(false)
  const [fbDone, setFbDone] = useState(false)
  const canFeedback = !!studentId
  const fbHasInput = fbRating != null || !!fbExp.trim() || !!fbFeat.trim() || !!fbProj.trim()
  async function submitFeedback() {
    if (!studentId) { onComplete(); return }
    setFbBusy(true)
    try {
      await supabase.from('ai_lab_feedback').insert({
        student_id: studentId,
        project_key: project.key,
        rating: fbRating,
        experience:        fbExp.trim()  || null,
        wishlist_features: fbFeat.trim() || null,
        wishlist_projects: fbProj.trim() || null,
      })
    } finally {
      setFbBusy(false); setFbDone(true)
    }
  }
  const model: LabModel | null = mIdx == null ? null : project.models[mIdx]
  const isReg = project.taskType === 'regression'
  const metricUnit = isReg ? '' : '%'
  const bNum = parseFloat(baselineInput)
  const rNum = parseFloat(resultInput)
  const haveNums = baselineInput !== '' && resultInput !== '' && !isNaN(bNum) && !isNaN(rNum)
  const conclusion = !haveNums ? '—'
    : rNum > bNum
      ? `The model scored ${resultInput}${metricUnit}, above the baseline of ${baselineInput}${metricUnit} — evidence that the features can tell us ${vars?.dv ?? 'the target'}, supporting the hypothesis.`
      : `The model scored ${resultInput}${metricUnit}, not above the baseline of ${baselineInput}${metricUnit}. That is a real result too — these features alone may not be enough, or the model or setup needs more work.`

  // The abstract sections (CSEF-style), shared by the on-screen preview and the print.
  const procedure = `Using the ${project.dataset} dataset (features/IV: ${(vars?.features ?? []).join(', ')}; target/DV: ${vars?.dv ?? ''}), I built a ${model?.name ?? 'model'}: load the dataset, select the features, split into training and validation, train the model, and evaluate it against a majority-class baseline.`
  const abstractSections: [string, string][] = [
    ['Purpose', chosenQuestion],
    ['Hypothesis', vars?.hypothesis ?? ''],
    ['Procedure', procedure],
    ['Results', haveNums ? `Baseline: ${baselineInput}${metricUnit}. ${model?.name}: ${resultInput}${metricUnit}.` : ''],
    ['Conclusion', conclusion],
  ]

  const today = fmtDate(new Date().toISOString())
  const next = () => goTo(active + 1)
  const back = () => goTo(Math.max(0, active - 1))

  // The same category description students see in Step 1, shown here on demand.
  const [catInfo, setCatInfo] = useState<{ short_description: string; long_description: string | null } | null>(null)
  const [catOpen, setCatOpen] = useState(false)
  useEffect(() => {
    let cancelled = false
    supabase.from('curated_category')
      .select('short_description, long_description')
      .eq('name', project.scholarCategory).maybeSingle()
      .then(({ data }) => { if (!cancelled) setCatInfo((data as typeof catInfo) ?? null) })
    return () => { cancelled = true }
  }, [project.scholarCategory])

  return (
    <Box>
      <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1 }}>All projects</Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>{project.title}</Typography>
        <Chip label={project.application} size="small" color="primary" variant="outlined" />
        {project.deepLearning && <Chip label="Deep Learning" size="small" color="secondary" variant="outlined" />}
        {project.advanced && <Chip label="Advanced" size="small" color="warning" variant="outlined" />}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, lineHeight: 1.6 }}>
        Practice run — follow along and notice the <strong>decision</strong> at each step. In your own project you'll make these yourself.
      </Typography>
      {(() => { const a = applicationLearnMore(project.application); return (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          New to this? Learn more about{' '}
          <Link href={a.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            {a.label}<OpenInNewIcon sx={{ fontSize: 13 }} />
          </Link>.
        </Typography>
      ) })()}

      <Stepper nonLinear activeStep={active} orientation="vertical">
        {/* 1 — Dataset */}
        <Step>
          <StepButton onClick={() => goTo(0)} disabled={maxStep < 0}>Select Research Category and Dataset</StepButton>
          <StepContent>
            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
              Suppose you're interested in{' '}
              <Link component="button" type="button" onClick={() => setCatOpen(true)}
                sx={{ fontWeight: 700, verticalAlign: 'baseline' }}>
                {project.scholarCategory}
              </Link>. You'd start by picking a dataset in that area to investigate —
              and for this exercise, we've selected one for you (in your own project, you'd choose it yourself in Step 1):
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}><strong>{project.dataset}</strong></Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6 }}>{detail?.full ?? project.datasetNote}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
              This dataset was used in the following publication:
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.6 }}>
              {project.citation}
              {project.citationUrl && <>{' '}<Link href={project.citationUrl} target="_blank" rel="noopener noreferrer">{project.citationUrl.replace(/^https?:\/\//, '')}</Link></>}
            </Typography>

            <Accordion variant="outlined" disableGutters sx={{ '&:before': { display: 'none' }, borderRadius: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableChartOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Explore the dataset</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {detail?.sample ? (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      First 5 rows{detail.sample.note ? ` ${detail.sample.note}` : ''}:
                    </Typography>
                    <Box sx={{ overflowX: 'auto', my: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.78rem' }}>
                        <Box component="thead"><Box component="tr">
                          {detail.sample.columns.map((c, i) => (
                            <Box key={i} component="th" sx={{ textAlign: 'left', p: 0.75, borderBottom: '2px solid', borderColor: 'divider', whiteSpace: 'nowrap', fontWeight: 700, color: c.includes('target') ? 'primary.main' : 'text.primary' }}>{c}</Box>
                          ))}
                        </Box></Box>
                        <Box component="tbody">
                          {detail.sample.rows.map((r, ri) => (
                            <Box key={ri} component="tr">
                              {r.map((cell, ci) => (
                                <Box key={ci} component="td" sx={{ p: 0.75, borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap', maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cell}</Box>
                              ))}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                    <Teach>
                      Each <strong>row</strong> is an <strong>example</strong> — one real case. Each <strong>column</strong> is a{' '}
                      <strong>feature</strong> (an input) except the <strong>target</strong>, which is what you predict. A model learns the
                      pattern from <strong>many</strong> examples like these (often thousands), and <strong>you choose which features</strong> to
                      feed it. Picking good features and having enough examples is how you train an AI model for your research.
                    </Teach>
                  </>
                ) : detail?.sampleImages ? (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      A few real examples from the dataset:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 1 }}>
                      {Array.from({ length: detail.sampleImages }, (_, i) => (
                        <Box
                          key={i}
                          component="img"
                          src={`${process.env.PUBLIC_URL}/lab-samples/${project.key}/${i + 1}.png`}
                          alt={`Example ${i + 1} from ${project.title}`}
                          loading="lazy"
                          sx={{
                            width: 72, height: 72, objectFit: 'cover',
                            imageRendering: 'pixelated',   // honest: these are low-res pixel images
                            border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover',
                          }}
                        />
                      ))}
                    </Box>
                    {detail.imageNote && <Teach>{detail.imageNote}</Teach>}
                  </>
                ) : detail?.sampleCode ? (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      This set is large, so it downloads in Colab — paste this to see a few examples:
                    </Typography>
                    <Box sx={{ position: 'relative', my: 1 }}>
                      <Box component="pre" sx={{
                        m: 0, p: 1.5, pr: 7, borderRadius: 1, bgcolor: '#0f2b4c', color: '#e6edf3',
                        fontSize: '0.72rem', lineHeight: 1.5, overflowX: 'auto', fontFamily: 'monospace',
                      }}>
                        {detail.sampleCode}
                      </Box>
                      <Button
                        size="small"
                        onClick={() => navigator.clipboard?.writeText(detail.sampleCode!)}
                        sx={{ position: 'absolute', top: 6, right: 6, minWidth: 0, color: '#e6edf3' }}
                      >
                        Copy
                      </Button>
                    </Box>
                    {detail.imageNote && <Teach>{detail.imageNote}</Teach>}
                  </>
                ) : detail ? (
                  <Teach>{detail.imageNote}</Teach>
                ) : null}
              </AccordionDetails>
            </Accordion>
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" startIcon={<InfoOutlinedIcon fontSize="small" />} onClick={() => setDsHelpOpen(true)}>
                Learn how to select a dataset
              </Button>
              <Button variant="contained" size="small" onClick={next}>Continue</Button>
            </Box>
          </StepContent>
        </Step>

        {/* 2 — Ask a research question (teaching + pick, combined) */}
        <Step>
          <StepButton onClick={() => goTo(1)} disabled={maxStep < 1}>Ask a Scientific Inquiry Research Question</StepButton>
          <StepContent>
            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
              For a dataset in a category you can ask <strong>different research questions</strong> depending on the
              features it contains. Study the features, then ask a question that <strong>relates the features to the
              target</strong>. Predicting a category is <strong>classification</strong>; predicting a number is{' '}
              <strong>regression</strong>. For this exercise, here are a few you could ask — pick one to explore:
            </Typography>
            <PickList<LabQuestion> items={project.questions} index={qIdx} onPick={setQIdx} render={(q, sel) => (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {sel && <CheckCircleIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{q.text} <Chip label={q.tag} size="small" sx={{ ml: 0.5 }} /></Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block', mt: 0.25 }}>{q.note}</Typography>
                </Box>
              </Box>
            )} />
            {qIdx != null && (
              project.questions[qIdx].recommended
                ? <Teach>Great starting point. Your <strong>baseline</strong> to beat is “{project.baselineLabel}” — about <strong>{fmtMetric(project, project.baseline)}</strong>. Anything above that means your features carry real signal.</Teach>
                : <Teach>A real question! For this practice run we'll follow the recommended one — it's the cleanest way to see the whole process. Save this angle for your own project.</Teach>
            )}
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}><Button size="small" onClick={back}>Back</Button><Button variant="contained" size="small" disabled={qIdx == null} onClick={next}>Continue</Button></Box>
          </StepContent>
        </Step>

        {/* 3 — Background research (topic + models) */}
        <Step>
          <StepButton onClick={() => goTo(2)} disabled={maxStep < 2}>Do Background Research</StepButton>
          <StepContent>
            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
              Before building anything, do your <strong>background research</strong> — the same as Step 3 of the real
              research process. For an AI project you research <strong>two</strong> things:
            </Typography>
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>1. Your topic</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, display: 'block' }}>
                Good research starts by learning what others have already found. Begin with the paper cited for this
                dataset (you saw it in the previous step): search its title on Google, read it, and write a short
                summary in your own words in your notebook. That's the heart of background research.
              </Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>2. The models</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, display: 'block', mb: 0.75 }}>
                To answer a research question, you can choose from many different AI models. Part of your background
                research is deciding which models to use — and understanding how each one works. For this exercise,
                we've pre-selected a few for you to study. A great way to learn them is to ask an AI tool like Claude,
                ChatGPT, or Gemini. If you'd like some help getting started, click below.
              </Typography>
              {project.models.map((m, i) => {
                const t = techniqueFor(m.name)
                return (
                  <Box key={i} sx={{ mb: 1.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block' }}>{m.blurb}</Typography>
                    {t && (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block', mt: 0.25 }}>{t.how}</Typography>
                        <Link href={t.learnMore} target="_blank" rel="noopener noreferrer" variant="caption"
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, mt: 0.25 }}>
                          Learn more about {t.label}<OpenInNewIcon sx={{ fontSize: 12 }} />
                        </Link>
                      </>
                    )}
                  </Box>
                )
              })}
              <Button size="small" variant="outlined" startIcon={<InfoOutlinedIcon fontSize="small" />} onClick={() => setLearnOpen(true)}>
                What should I learn?
              </Button>
            </Box>
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}><Button size="small" onClick={back}>Back</Button><Button variant="contained" size="small" onClick={next}>Continue</Button></Box>
          </StepContent>
        </Step>

        {/* 4 — Formulate a hypothesis (model = apparatus; IV / DV / CV) */}
        <Step>
          <StepButton onClick={() => goTo(3)} disabled={maxStep < 3}>Formulate a Hypothesis</StepButton>
          <StepContent>
            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
              You now have a <strong>research question</strong> and a <strong>dataset</strong>. A hypothesis connects the
              variables in your data. There are three:
            </Typography>
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2"><strong>IV — Independent Variables</strong> (the features you'll use):</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 0.75, '& li': { mb: 0.1 } }}>
                {(vars?.features ?? []).map((f, i) => (
                  <Typography key={i} component="li" variant="body2" color="text.secondary">{f}</Typography>
                ))}
              </Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>DV — Dependent Variable</strong> (the target): {vars?.dv ?? 'the target'}.</Typography>
              <Typography variant="body2"><strong>CV — Control Variables</strong> (feature values you use to focus on a subset of the data): {vars?.cv ?? 'optional'}.</Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
              Now connect the IV and DV into a hypothesis for your question:
            </Typography>
            <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}><strong>Research Question:</strong> {chosenQuestion}</Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.6, mt: 0.75 }}><strong>Hypothesis:</strong> {vars?.hypothesis}</Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Now let's select a model to test this hypothesis. From the background research you did, which would you use?
            </Typography>
            <PickList<LabModel> items={project.models} index={mIdx} onPick={setMIdx} render={(m, sel) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {sel && <CheckCircleIcon color="primary" fontSize="small" />}
                <Box><Typography variant="body2" sx={{ fontWeight: 700 }}>{m.name}</Typography><Typography variant="caption" color="text.secondary">{m.blurb}</Typography></Box>
              </Box>
            )} />
            {mIdx != null && model && (
              <Teach>The model is your instrument for testing the hypothesis — in a hands-on experiment, this is your apparatus. You'll run <strong>{model.name}</strong> next.</Teach>
            )}
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}><Button size="small" onClick={back}>Back</Button><Button variant="contained" size="small" disabled={mIdx == null} onClick={next}>Continue</Button></Box>
          </StepContent>
        </Step>

        {/* 6 — Prototype */}
        <Step>
          <StepButton onClick={() => goTo(4)} disabled={maxStep < 4}>Run a prototype</StepButton>
          <StepContent>
            {model && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  The AI split the data (80% train / 20% test), trained a default <strong>{model.name}</strong> on a
                  subset, and scored it on the held-out test set:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
                  <Card variant="outlined" sx={{ flex: '1 1 160px' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Baseline ({project.baselineLabel})</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{fmtMetric(project, project.baseline)}</Typography>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" sx={{ flex: '1 1 160px', borderColor: 'success.light' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Your {model.name}</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: 'success.main' }}>{fmtMetric(project, model.score)}</Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Teach>
                  Read it against the baseline: {fmtMetric(project, model.score)} vs. {fmtMetric(project, project.baseline)} means the features really do{' '}
                  {isReg ? 'explain the outcome' : 'predict the target'} — far better than guessing.{' '}
                  {project.key === 'species' && mIdx === 2 && 'Notice KNN did worse — it’s sensitive to feature scaling. A great example of why preprocessing matters. '}
                  But this is a <strong>quick prototype</strong>: one subset, default settings. A real project checks for
                  data leakage, runs several splits, and compares models — that's the rigor you'll add yourself.
                </Teach>
                {project.deepLearning && (
                  <Teach>
                    This is a <strong>real-world problem</strong>, and the code here runs on a <strong>small subset for just a
                    few epochs</strong> — enough to prove the method works and the code runs end to end. Your accuracy will look
                    modest, and that's expected. In the real experiment you'd train on the <strong>full dataset</strong>, run{' '}
                    <strong>many more epochs</strong>, and tune the network to push the results much higher.
                  </Teach>
                )}

                {(() => {
                  const code = buildColabCode(project.key, model.name)
                  return (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Build it yourself — real code, real results</Typography>
                      {code ? (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6 }}>
                            Here's the actual Python code — load the dataset, prepare the features, train the model, and
                            read the results against a baseline. Open Google Colab (a free notebook in your browser),
                            paste it in, and run it to get your own numbers.
                          </Typography>
                          <Box component="pre" sx={{ p: 1.5, m: 0, bgcolor: 'grey.900', color: 'grey.100', borderRadius: 1, overflowX: 'auto', fontSize: '0.72rem', lineHeight: 1.55, fontFamily: 'monospace', whiteSpace: 'pre' }}>{code}</Box>
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button size="small" variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />}
                              onClick={() => { navigator.clipboard?.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500) }}>
                              {copied ? 'Copied!' : 'Copy code'}
                            </Button>
                            <Button size="small" variant="contained" component="a" href="https://colab.research.google.com/#create=true" target="_blank" rel="noopener noreferrer">
                              Open Google Colab
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          A ready-to-run notebook for this dataset is coming soon. The five steps are the same: load the dataset
                          → select your features → split into training and validation → run the model → get the results (and
                          compare to the baseline).
                        </Typography>
                      )}
                    </Box>
                  )
                })()}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: 'primary.main', color: '#fff' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>You have a result!</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.92 }}>
                    Dataset → question → background research → hypothesis → result, and a decision at every step. The last
                    thing a researcher does is write it up — next, turn your work into a one-page project summary you can print.
                  </Typography>
                </Box>
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}><Button size="small" onClick={back}>Back</Button><Button variant="contained" size="small" onClick={next}>Continue</Button></Box>
              </>
            )}
          </StepContent>
        </Step>

        {/* 6 — Write a report */}
        <Step>
          <StepButton onClick={() => goTo(5)} disabled={maxStep < 5}>Write a Project Summary</StepButton>
          <StepContent>
            <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>
              A scientist writes up what they did and found. Enter the two numbers the code printed — your baseline and
              your model's result — and we'll lay out a one-page project summary, formatted like your research journal,
              ready to print.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <TextField size="small" type="number" label={isReg ? 'Baseline (R²)' : 'Baseline (%)'} value={baselineInput} onChange={e => setBaselineInput(e.target.value)} sx={{ flex: '1 1 140px' }} />
              <TextField size="small" type="number" label={isReg ? 'Your result (R²)' : 'Your result (%)'} value={resultInput} onChange={e => setResultInput(e.target.value)} sx={{ flex: '1 1 140px' }} />
            </Box>

            {/* One-page, print-ready summary — same paper look as the research journals */}
            <J.Page id="lab-summary-print">
              <J.Header title={project.title} subtitle="Ardent Scholar — Sample Project Summary" date={today} />
              {(studentName || studentSchool) && (
                <Jt label="Researcher" value={[studentName, studentSchool].filter(Boolean).join(' · ')} />
              )}
              <Jt label="Category" value={`${project.scholarCategory} · Data Science / AI Research`} />
              {abstractSections.map(([label, text]) => (
                <React.Fragment key={label}>
                  <J.Section>{label.toUpperCase()}</J.Section>
                  <J.Body sx={{ whiteSpace: 'pre-wrap' }}>{text || (label === 'Results' ? 'Enter your baseline and result above to fill this in.' : '—')}</J.Body>
                </React.Fragment>
              ))}
              <J.Section>REFERENCE</J.Section>
              <J.Body sx={{ fontSize: '9.5pt', color: '#333' }}>{project.citation}</J.Body>
              <J.Section>ACKNOWLEDGEMENT</J.Section>
              <J.Body sx={{ fontSize: '9.5pt', color: '#333' }}>
                This report was generated by Ardent Scholar — scholar.ardentlabs.io
              </J.Body>
            </J.Page>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              <Button size="small" onClick={back}>Back</Button>
              <Button size="small" variant="outlined" startIcon={<PrintIcon fontSize="small" />} disabled={!haveNums} onClick={() => handlePrint('lab-summary-print')}>Print summary</Button>
              <Button size="small" variant="contained" onClick={next}>Continue → give feedback</Button>
            </Box>
          </StepContent>
        </Step>

        {/* 7 — Give feedback */}
        <Step>
          <StepButton onClick={() => goTo(6)} disabled={maxStep < 6}>Give Feedback</StepButton>
          <StepContent>
            {fbDone ? (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>Thank you! Your feedback goes straight to the team and helps decide what we build next.</Alert>
                <Button size="small" variant="contained" onClick={onComplete}>Done — back to my projects</Button>
              </>
            ) : !canFeedback ? (
              <>
                <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                  This is where students tell us how the project went and what they'd like to see next. (Feedback is saved from a student account.)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" onClick={back}>Back</Button>
                  <Button size="small" variant="contained" onClick={onComplete}>Done — back to my projects</Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                  One last thing — how was it? This is a brand-new tutorial, and your feedback shapes what we build next. Everything here is optional.
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>How was this project?</Typography>
                  <Rating value={fbRating} onChange={(_, v) => setFbRating(v)} />
                </Box>
                <TextField fullWidth multiline minRows={2} size="small" sx={{ mb: 1.5 }}
                  label="What did you enjoy, or find confusing?" value={fbExp} onChange={e => setFbExp(e.target.value)} />
                <TextField fullWidth multiline minRows={2} size="small" sx={{ mb: 1.5 }}
                  label="What would make the Lab better? (features you'd like)" value={fbFeat} onChange={e => setFbFeat(e.target.value)} />
                <TextField fullWidth multiline minRows={2} size="small" sx={{ mb: 2 }}
                  label="What topics or project types would you like to try next?" value={fbProj} onChange={e => setFbProj(e.target.value)} />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" onClick={back}>Back</Button>
                  <Button size="small" onClick={onComplete}>Skip</Button>
                  <Button size="small" variant="contained" disabled={fbBusy || !fbHasInput} onClick={submitFeedback}>
                    {fbBusy ? 'Sending…' : 'Submit & finish'}
                  </Button>
                </Box>
              </>
            )}
          </StepContent>
        </Step>
      </Stepper>

      {/* Category description — the same one students see in Step 1 */}
      <Dialog open={catOpen} onClose={() => setCatOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{project.scholarCategory}</DialogTitle>
        <DialogContent>
          {catInfo ? (
            <>
              {catInfo.short_description && (
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{catInfo.short_description}</Typography>
              )}
              {catInfo.long_description && (
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {catInfo.long_description}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setCatOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Short tutorial: how to select a dataset */}
      <Dialog open={dsHelpOpen} onClose={() => setDsHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>How to select a dataset</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            For this exercise we picked a dataset for you. In your own project you'd choose one — here's what to look for:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
            <Typography component="li" variant="body2"><strong>A clear target</strong> — the thing you want to predict (a category or a number).</Typography>
            <Typography component="li" variant="body2"><strong>Useful features</strong> — inputs that plausibly relate to the target.</Typography>
            <Typography component="li" variant="body2"><strong>Enough examples</strong> — more rows help a model learn the pattern (hundreds at least; thousands are better).</Typography>
            <Typography component="li" variant="body2"><strong>Clean and labeled</strong> — organized, with the target filled in for each row.</Typography>
            <Typography component="li" variant="body2"><strong>Public and licensed</strong> — free to use, and no private or personal data (this keeps your project safe for the fair's review).</Typography>
            <Typography component="li" variant="body2"><strong>Relevant</strong> — it can actually answer a question you care about.</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.6 }}>
            Good places to find datasets:{' '}
            <Link href="https://www.kaggle.com/datasets" target="_blank" rel="noopener noreferrer">Kaggle</Link>, the{' '}
            <Link href="https://archive.ics.uci.edu/" target="_blank" rel="noopener noreferrer">UCI Machine Learning Repository</Link>,
            and government or research open-data sites.
          </Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setDsHelpOpen(false)}>Got it</Button></DialogActions>
      </Dialog>

      {/* What should I learn? — AI prompts for researching a model */}
      <Dialog open={learnOpen} onClose={() => setLearnOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Research a model with AI</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Open an AI tool (like Claude, ChatGPT, or Gemini) and ask it about each model. Replace <strong>[model]</strong> with the
            model's name — for example, {project.models[0]?.name}. Good prompts to start with:
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
            <Typography component="li" variant="body2">“Explain how <strong>[model]</strong> works, in simple terms for a high-school student.”</Typography>
            <Typography component="li" variant="body2">“How does <strong>[model]</strong> make a prediction, step by step?”</Typography>
            <Typography component="li" variant="body2">“What are the strengths and weaknesses of <strong>[model]</strong>?”</Typography>
            <Typography component="li" variant="body2">“When is <strong>[model]</strong> a good choice, and when is it not?”</Typography>
            <Typography component="li" variant="body2">“Show a small worked example of <strong>[model]</strong> on a simple dataset.”</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.6 }}>
            Do this for each of the {project.models.length} models above, and you'll be ready to choose.
          </Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setLearnOpen(false)}>Got it</Button></DialogActions>
      </Dialog>
    </Box>
  )
}

// ── Menu + gating ───────────────────────────────────────────────────────────────

export default function AiResearchLab() {
  const { session, studentLifecycle, role } = useAuth()
  const { studentId: viewerStudentId, viewerIsStudent } = useViewerStudent()
  // Staff exploring the Lab as themselves (not viewing-as a student): no student
  // record, so nothing they do here is saved — surface that as a self-study note.
  const staffSelfStudy = !viewerStudentId && role != null && role !== 'student' && role !== 'parent'

  const [taken,    setTaken]    = useState<Set<string>>(new Set())
  const [loaded,   setLoaded]   = useState(false)
  const [openKey,  setOpenKey]  = useState<string | null>(null)
  const [upsell,   setUpsell]   = useState(false)
  const [school,   setSchool]   = useState('')
  const [name,     setName]     = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!viewerStudentId) { setLoaded(true); return }
      const { data } = await supabase.from('ai_lab_project')
        .select('project_key').eq('student_id', viewerStudentId)
      if (cancelled) return
      setTaken(new Set((data ?? []).map((r: { project_key: string }) => r.project_key)))
      const { data: sp } = await supabase.from('student_profile')
        .select('school_name, school_city, school_state, user_id')
        .eq('student_id', viewerStudentId).maybeSingle()
      if (cancelled) return
      const prof = sp as { school_name: string | null; school_city: string | null; school_state: string | null; user_id: string } | null
      setSchool([prof?.school_name, prof?.school_city, prof?.school_state].filter(Boolean).join(', '))
      if (prof?.user_id) {
        const { data: u } = await supabase.from('users')
          .select('first_name, last_name').eq('user_id', prof.user_id).maybeSingle()
        const usr = u as { first_name: string | null; last_name: string | null } | null
        if (!cancelled) setName(`${usr?.first_name ?? ''} ${usr?.last_name ?? ''}`.trim())
      }
      setLoaded(true)
    })()
    return () => { cancelled = true }
  }, [viewerStudentId])

  const lifecycleState = studentLifecycle?.lifecycle_state ?? null
  const isTrial = lifecycleState === 'trial_active' || lifecycleState === 'trial_expired'
    || (lifecycleState === null && session?.user?.user_metadata?.is_trial === true)
  // Trial students lock the rest once they've started one (and it isn't this one).
  const gateActive = viewerIsStudent && isTrial
  const locked = (key: string) => gateActive && taken.size >= 1 && !taken.has(key)

  async function openProject(p: LabProject) {
    if (locked(p.key)) { setUpsell(true); return }
    if (viewerIsStudent && viewerStudentId && !taken.has(p.key)) {
      await supabase.from('ai_lab_project')
        .upsert({ student_id: viewerStudentId, project_key: p.key }, { onConflict: 'student_id,project_key', ignoreDuplicates: true })
      setTaken(prev => new Set(prev).add(p.key))
    }
    setOpenKey(p.key)
  }

  async function complete(key: string) {
    if (viewerIsStudent && viewerStudentId) {
      await supabase.from('ai_lab_project')
        .update({ completed_at: new Date().toISOString() })
        .eq('student_id', viewerStudentId).eq('project_key', key)
    }
    setOpenKey(null)
  }

  const openProjectObj = LAB_PROJECTS.find(p => p.key === openKey) ?? null

  if (!loaded) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: openProjectObj ? 820 : 1240, mx: 'auto' }}>
      {openProjectObj ? (
        <Walkthrough project={openProjectObj} studentName={name} studentSchool={school} studentId={viewerStudentId} onBack={() => setOpenKey(null)} onComplete={() => complete(openProjectObj.key)} />
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <InsightsOutlinedIcon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Ardent AI Research Lab</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Pick a project and walk through how you use AI and a dataset to answer a scientific question — a
            decision at every step.{staffSelfStudy ? '' : " It's practice; your real project comes next."}
          </Typography>

          {gateActive && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {taken.size >= 1
                ? 'As a trial student you can complete one Lab project. Enroll to unlock the rest.'
                : 'As a trial student you can complete one Lab project free — pick the one that excites you most.'}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' } }}>
            {LAB_PROJECTS.map(p => {
              const isLocked = locked(p.key)
              const isDone   = taken.has(p.key)
              return (
                <Card key={p.key} variant="outlined"
                  onClick={() => openProject(p)}
                  sx={{ cursor: 'pointer', opacity: isLocked ? 0.6 : 1, position: 'relative',
                    '&:hover': { borderColor: isLocked ? 'divider' : 'primary.light' } }}>
                  <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{p.title}</Typography>
                      {isLocked && <LockOutlinedIcon fontSize="small" color="disabled" />}
                      {isDone && <CheckCircleIcon fontSize="small" color="success" />}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.75, flexWrap: 'wrap' }}>
                      <Chip label={p.application} size="small" variant="outlined" />
                      <Chip label={p.scholarCategory} size="small" />
                      {p.deepLearning && <Chip label="Deep Learning" size="small" color="secondary" variant="outlined" />}
                      {p.advanced && <Chip label="Advanced" size="small" color="warning" variant="outlined" />}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>{p.hook}</Typography>
                    {isLocked && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                        Enroll to unlock
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </Box>
        </>
      )}

      <Dialog open={upsell} onClose={() => setUpsell(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Unlock all Lab projects</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Your free trial includes one AI Research Lab project. Enroll to work through all of them — anytime during
            your project — and to start your own AI-enabled research.
          </Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setUpsell(false)}>Got it</Button></DialogActions>
      </Dialog>
    </Box>
  )
}
